import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { SIGNUPS_QUERY_KEY, SignupEntry } from './useSignups'

/**
 * VRG-Pricing.Access.Gate — grant / revoke a waitlist entry's access to the
 * gated `/pricing` page on the marketing site.
 *
 * ⚠️ DELIBERATELY SEPARATE from `useGrantSignup` / `useRejectSignup`. Those
 * decide whether someone may create a Vera account; these decide whether they
 * may read Vera's price list. Folding them together would have made every
 * waitlist approval publish the catalogue, and made "take their pricing link
 * away" indistinguishable from "take their platform access away".
 *
 * Both routes return the UPDATED `SignupEntry`, so the grid's pricing columns
 * come back correct from the invalidation rather than from an optimistic guess.
 *
 * ## ⚠️ `...options` GOES FIRST. It is not a style choice, it is the bug.
 *
 * Both hooks used to close with `onSuccess: <wrapper>, onError, ...options`, and
 * an object literal's LAST key wins — so spreading the caller's options after the
 * wrapper silently replaced it. A caller that passed only `onError` kept the
 * wrapper and invalidated; a caller that passed `onSuccess` (a toast) deleted the
 * `invalidateQueries` call along with it.
 *
 * That asymmetry is exactly what the Pricing access column showed: Revoke passes
 * `onError` alone and its pill flipped immediately, Grant passes a success toast
 * and its pill only moved on a hard reload. Measured in the browser — the DELETE
 * was followed by `GET /internal/signups`, the POST was not.
 *
 * So the spread sits BEFORE the handlers, which makes the wrapper the last word
 * and the caller's callback something it CALLS rather than something that
 * replaces it. `onError` is left to the spread; re-declaring it after would be
 * the same overwrite in the other direction.
 */

export type PricingAccessPayload = {
  signupId: string
}

/**
 * `POST /internal/signups/:id/pricing-access`
 *
 * ⚠️ ONE action, not two: it stamps the grant AND emails a single-use, 7-day
 * link. A grant nobody was told about is indistinguishable, from the
 * prospect's side, from no grant at all.
 *
 * So a FAILURE HERE IS REAL and must be surfaced. The API writes the grant
 * columns before sending, then rethrows if the send fails, so a 500 means "the
 * row is granted and the email did not go out" — usually PRICING_SITE_URL
 * unset. Retrying is safe and idempotent. Do not swallow this into a success
 * toast; that reproduces the exact confusion the combined action exists to
 * avoid.
 */
export const useGrantPricingAccess = (
  options?: Omit<UseMutationOptions<SignupEntry, Error, PricingAccessPayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()

  return useMutation<SignupEntry, Error, PricingAccessPayload>({
    mutationFn: async ({ signupId }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/internal/signups/${signupId}/pricing-access`,
      })
      if (response.status >= 400) {
        throw new Error(response.data?.message || 'Failed to grant pricing access')
      }
      return response.data as SignupEntry
    },
    // ⚠️ Spread FIRST — see the module docblock. Last key wins, and this wrapper
    // has to be the last word or the invalidation disappears with the caller's toast.
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

/**
 * `DELETE /internal/signups/:id/pricing-access`
 *
 * Stamps the revocation AND revokes every outstanding unused pricing token for
 * that address, so links already sitting in an inbox stop working.
 *
 * ⚠️ What it does NOT reach, because the control reads as more absolute than it
 * is: an already-issued session on the pricing site (that cookie is
 * self-contained and is not re-checked per request), and anyone who also holds
 * a platform login — signing in there is a separate door that does not consult
 * this row at all. Immediate global revocation is a secret rotation on the
 * marketing site, not this button.
 */
export const useRevokePricingAccess = (
  options?: Omit<UseMutationOptions<SignupEntry, Error, PricingAccessPayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()

  return useMutation<SignupEntry, Error, PricingAccessPayload>({
    mutationFn: async ({ signupId }) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/internal/signups/${signupId}/pricing-access`,
      })
      if (response.status >= 400) {
        throw new Error(response.data?.message || 'Failed to revoke pricing access')
      }
      return response.data as SignupEntry
    },
    // ⚠️ Spread FIRST — see the module docblock.
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}
